import React, { useEffect } from 'react';
import {
  Form,
  FormInstance,
  DateSerialized,
  SelectBooleanInput,
  Checkbox
} from 'blueprint-form';
import { Row, Col } from '../../../../grid';
import { StartDate } from '../date';
import { EndDate } from '../date';
import SelectTime from '../time';
import SelectLanguage from '../language';
import Timezone from '../timezone';
import { CardBlock } from '../../card';
import {
  GetCurrentTZ,
  GetFormalDate,
  ShorthandDate
} from '../../../../libs/time';

interface EpxEndodbData {
  method: string;
  language: string;
  start: string;
  end?: string;
  tz: string;
  times: string[];
  config: {
    M: {
      pre: boolean;
      post: boolean;
    };
    A: {
      pre: boolean;
      post: boolean;
    };
    E: {
      pre: boolean;
      post: boolean;
    };
    L?: {
      pre: boolean;
      post: boolean;
    };
  };
  includeInsulin: boolean;
  isInsulinLongActing: boolean;
}

type EpxEndodbDataRaw = DateSerialized<EpxEndodbData, 'start' | 'end'>;

export const EpxEndodbDefaultData: EpxEndodbData = {
  method: 'message',
  language: 'en',
  start: GetFormalDate(ShorthandDate.Today),
  end: null,
  tz: GetCurrentTZ(),
  times: ['10:00', '14:00', '19:00', '21:00'],
  config: {
    M: {
      pre: true,
      post: true
    },
    A: {
      pre: true,
      post: true
    },
    E: {
      pre: true,
      post: true
    },
    L: {
      pre: false,
      post: false
    }
  },
  includeInsulin: false,
  isInsulinLongActing: false
};

const DATE_FORMAT = 'YYYY-MM-DD';
const { DeserializeDate, SerializeDate } = FormInstance;

interface EpxEndodbFormProps {
  setForm?: (form: FormInstance<EpxEndodbDataRaw>) => void;
  onSubmit?: (data: EpxEndodbData) => void;
  isDisabled?: boolean;
}

const EpxEndodbForm = ({
  setForm,
  onSubmit: _onSubmit,
  isDisabled
}: EpxEndodbFormProps): JSX.Element => {
  const serialize = (data: EpxEndodbDataRaw): EpxEndodbData => {
    return {
      ...data,
      start: SerializeDate(data.start, DATE_FORMAT),
      end: SerializeDate(data.end, DATE_FORMAT),
      times: data.times
    };
  };
  const deserialize = (data: EpxEndodbData): EpxEndodbDataRaw => {
    return {
      ...data,
      start: DeserializeDate(data.start, DATE_FORMAT),
      end: DeserializeDate(data.end, DATE_FORMAT)
    };
  };
  const defaultData = deserialize(EpxEndodbDefaultData);
  const form = new FormInstance<EpxEndodbDataRaw, EpxEndodbData>(defaultData, {
    serialize,
    deserialize
  });
  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  const onSubmit = (data: EpxEndodbData): void => {
    _onSubmit(data);
  };
  return (
    <CardBlock bare>
      <Form form={form} onSubmit={onSubmit} disabled={isDisabled}>
        {(props) => {
          return (
            <>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <StartDate label="Start Date" name="start" fill />
                  </Col>
                  <Col sm={12}>
                    <EndDate label="End Date" name="end" />
                  </Col>
                  <Col sm={12}>
                    <Timezone label="Timezone" name="tz" fill />
                  </Col>
                  <Col sm={12}>
                    <SelectLanguage />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectTime
                      label="Breakfast Reading"
                      name="times[0]"
                      disabled={
                        !(
                          props.values.config.M.pre ||
                          props.values.config.M.post
                        )
                      }
                    />
                  </Col>
                  <Col
                    sm={6}
                    style={{
                      paddingTop: '40px'
                    }}
                  >
                    <Checkbox label="Collect Fasting" name="config.M.pre" />
                  </Col>
                  <Col
                    sm={6}
                    style={{
                      paddingTop: '40px'
                    }}
                  >
                    <Checkbox
                      label="Collect Postprandial"
                      name="config.M.post"
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12}>
                    <SelectTime
                      label="Lunch Reading"
                      name="times[1]"
                      disabled={
                        !(
                          props.values.config.A.pre ||
                          props.values.config.A.post
                        )
                      }
                    />
                  </Col>
                  <Col
                    sm={6}
                    style={{
                      paddingTop: '40px'
                    }}
                  >
                    <Checkbox label="Collect Fasting" name="config.A.pre" />
                  </Col>
                  <Col
                    sm={6}
                    style={{
                      paddingTop: '40px'
                    }}
                  >
                    <Checkbox
                      label="Collect Postprandial"
                      name="config.A.post"
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12}>
                    <SelectTime
                      label="Dinner Reading"
                      name="times[2]"
                      disabled={
                        !(
                          props.values.config.E.pre ||
                          props.values.config.E.post
                        )
                      }
                    />
                  </Col>
                  <Col
                    sm={6}
                    style={{
                      paddingTop: '40px'
                    }}
                  >
                    <Checkbox label="Collect Fasting" name="config.E.pre" />
                  </Col>
                  <Col
                    sm={6}
                    style={{
                      paddingTop: '40px'
                    }}
                  >
                    <Checkbox
                      label="Collect Postprandial"
                      name="config.E.post"
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12}>
                    <SelectTime
                      label="Late Reading"
                      name="times[3]"
                      disabled={
                        !(
                          props.values.config.L?.pre ||
                          props.values.config.L?.post
                        )
                      }
                    />
                  </Col>
                  <Col
                    sm={6}
                    style={{
                      paddingTop: '40px'
                    }}
                  >
                    <Checkbox label="Collect Fasting" name="config.L.pre" />
                  </Col>
                  <Col
                    sm={6}
                    style={{
                      paddingTop: '40px'
                    }}
                  >
                    <Checkbox
                      label="Collect Postprandial"
                      name="config.L.post"
                    />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Collect Insulin Units"
                      name="includeInsulin"
                      fill
                    />
                  </Col>
                  {props.values.includeInsulin && (
                    <Col sm={12}>
                      <SelectBooleanInput
                        label="Long-Acting Insulin"
                        name="isInsulinLongActing"
                        fill
                      />
                    </Col>
                  )}
                </Row>
              </CardBlock>
            </>
          );
        }}
      </Form>
    </CardBlock>
  );
};

export default EpxEndodbForm;
